import { Link } from "react-router-dom";
import { ArrowRight, Map, Target, PackageCheck, Zap, GitMerge, Brain, Inbox, FileSearch, Workflow } from "lucide-react";
import { Button } from "@/components/ui/button";
import SEO from "@/components/SEO";
import WorkflowConvergence from "@/components/graphics/WorkflowConvergence";
import ProofMetricCards from "@/components/graphics/ProofMetricCards";
import ProblemCards from "@/components/graphics/ProblemCards";
import ServiceCards, { type Service } from "@/components/graphics/ServiceCards";
import FaqAccordion from "@/components/FaqAccordion";

import AlfredCallout from "@/components/AlfredCallout";
import AppsShowcase from "@/components/appshub/AppsShowcase";
import { track } from "@/lib/forms";

const SERVICES: Service[] = [
  { badge: "5 to 10 days", icon: Zap, title: "Automation Sprint", body: "One bounded workflow, fixed and documented.", price: "From £750", cta: { label: "Discuss a Sprint", to: "/book?service=automation-sprint" } },
  { badge: "2 to 4 weeks", icon: GitMerge, title: "Ops Sync Build", body: "Connect sheets, forms and reporting into one flow.", price: "From £1,500", cta: { label: "Scope an Ops Build", to: "/book?service=ops-sync-build" } },
  { badge: "2 to 4 weeks", icon: Brain, title: "AI Workflow", body: "AI for triage, drafting and summaries, plus governed agents that use approved tools.", price: "From £1,500", cta: { label: "Start an AI Workflow", to: "/book?service=ai-workflow-pilot" } },
];

const AGENTS = [
  { i: Inbox, t: "Review and route", b: "Read an enquiry or request, check what is missing, classify it and send it to the right queue." },
  { i: FileSearch, t: "Research and prepare", b: "Gather information from approved sources, compare options and prepare a clear brief or draft with its sources attached." },
  { i: Workflow, t: "Coordinate the next step", b: "Monitor progress, update connected records and trigger the next approved action across tools." },
];

const STEPS = [
  { i: Map, t: "1. Map the workflow", b: "Walk through the current process and where time is lost." },
  { i: Target, t: "2. Find the first automation", b: "Identify the smallest reliable build that removes the most manual work." },
  { i: PackageCheck, t: "3. Scope a small build", b: "Get a written scope with timeline, price and acceptance criteria." },
];

const FAQ = [
  { q: "Do we need to know what to automate?", a: "No. Bring the process that wastes the most time and the diagnosis call decides what to do with it." },
  { q: "Is this only for Google Workspace?", a: "Google Workspace is the strongest fit, but we also work with n8n, Make, Zapier, Airtable, Python and Power Automate where useful." },
  { q: "Can you fix an existing Zapier or Make setup?", a: "Yes. Many projects start by simplifying or replacing fragile or expensive automations." },
  { q: "Will AI make decisions automatically?", a: "Not for anything sensitive. AI drafts, classifies and summarises. Humans review where judgement matters." },
  { q: "How quickly can a workflow be built?", a: "An Automation Sprint is usually delivered in 5 to 10 business days. Larger builds take 2 to 4 weeks." },
];

export default function Home() {
  return (
    <>
      <SEO
        title="AutomationTK | Practical Automation and Governed AI Agents"
        description="Practical workflow automation, AI-assisted systems and agentic AI for operations-heavy teams. Connect tools, reduce manual work and keep people in control."
        path="/"
        jsonLd={[
          {
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: FAQ.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })),
          },
        ]}
      />

      {/* HERO */}
      <section className="hero-pad bg-white" data-section="hero">
        <div className="container-atk grid gap-12 lg:gap-16 lg:grid-cols-[1.15fr,1fr] items-center">
          <div className="animate-fade-up">
            <span className="badge-pill">Workflow automation · AI-assisted workflows · AI agents</span>
            <h1 className="mt-5 text-[40px] sm:text-[48px] lg:text-[60px] font-extrabold text-navy leading-[1.04] tracking-tight">
              Your operations shouldn't run on copy-paste.
            </h1>
            <p className="mt-5 text-[17px] sm:text-[18px] text-muted-foreground leading-relaxed max-w-xl">
              AutomationTK builds quiet workflows and governed AI agents that move data, use approved tools, take the next approved action and free teams from busywork, while keeping people in control.
            </p>
            <div className="mt-7 flex flex-col sm:flex-row gap-3">
              <Button asChild className="rounded-full h-12 px-7 text-[15px] font-semibold" data-cta="book-workflow-diagnosis" onClick={() => track("cta_hero_book_click")}>
                <Link to="/book">Book a workflow diagnosis <ArrowRight className="ml-1 h-4 w-4" /></Link>
              </Button>
              <Button asChild variant="outline" className="rounded-full h-12 px-7 text-[15px] font-semibold border-border text-navy" onClick={() => track("cta_hero_usecases_click")}>
                <Link to="/use-cases">See what we automate</Link>
              </Button>
            </div>
          </div>
          <div className="lg:pl-4">
            <WorkflowConvergence />
          </div>
        </div>
      </section>

      {/* PROBLEM */}
      <section className="section-pad bg-soft" data-section="problem">
        <div className="container-atk">
          <div className="max-w-3xl">
            <h2 className="text-[30px] sm:text-[38px] lg:text-[44px] font-[750] text-navy">If your week looks like this, there's a cleaner way.</h2>
          </div>
          <div className="mt-10"><ProblemCards /></div>
        </div>
      </section>

      {/* WHAT WE BUILD */}
      <section className="section-pad bg-white" data-section="services">
        <div className="container-atk">
          <div className="max-w-3xl">
            <h2 className="text-[30px] sm:text-[38px] lg:text-[44px] font-[750] text-navy">What we build.</h2>
            <p className="mt-4 text-[16px] text-muted-foreground">One workflow at a time. Proven, then expanded.</p>
          </div>
          <div className="mt-10"><ServiceCards services={SERVICES} columns={3} /></div>
          <div className="mt-8">
            <Link to="/services" className="text-cta">See full service stack <ArrowRight className="h-4 w-4" /></Link>
          </div>
        </div>
      </section>

      {/* AI AGENTS */}
      <section className="section-pad bg-soft" data-section="ai-agents">
        <div className="container-atk">
          <div className="max-w-3xl">
            <span className="badge-pill">AI agents for operations</span>
            <h2 className="mt-4 text-[30px] sm:text-[38px] lg:text-[44px] font-[750] text-navy">When the work needs more than a fixed rule.</h2>
            <p className="mt-4 text-[16px] text-muted-foreground leading-relaxed">
              Traditional automations follow a set path. AI agents can handle work that needs context, several steps and more than one tool.
            </p>
            <p className="mt-4 text-[16px] text-muted-foreground leading-relaxed">
              We design agents that review information, choose from approved actions, update the right systems and escalate exceptions to a person. Each agent works within defined permissions, records what it does and asks for approval when needed.
            </p>
            <p className="mt-4 text-[16px] text-muted-foreground leading-relaxed">
              Put simply, this is agentic AI: more flexible than a fixed workflow, but still controlled, reviewable and tied to a clear business outcome.
            </p>
          </div>
          <div className="mt-10 grid gap-5 md:grid-cols-3">
            {AGENTS.map((a) => (
              <article key={a.t} className="card-atk">
                <span className="icon-circle"><a.i className="h-5 w-5" /></span>
                <h3 className="mt-5 text-[18px] font-semibold text-navy">{a.t}</h3>
                <p className="mt-2 text-[15px] text-muted-foreground leading-relaxed">{a.b}</p>
              </article>
            ))}
          </div>
          <p className="mt-8 text-[15px] text-muted-foreground max-w-3xl">
            Sensitive judgement, external communication and irreversible actions can remain behind human approval.
          </p>
        </div>
      </section>

      {/* PROOF */}
      <section className="section-pad bg-white" data-section="proof">
        <div className="container-atk">
          <div className="max-w-2xl">
            <h2 className="text-[30px] sm:text-[38px] lg:text-[44px] font-[750] text-navy">Hours back. Errors gone.</h2>
          </div>
          <div className="mt-10"><ProofMetricCards /></div>
        </div>
      </section>

      {/* DIAGNOSIS */}
      <section className="section-pad bg-soft" data-section="diagnosis">
        <div className="container-atk">
          <div className="max-w-3xl">
            <h2 className="text-[30px] sm:text-[38px] lg:text-[44px] font-[750] text-navy">How the diagnosis call works.</h2>
            <p className="mt-4 text-[16px] text-muted-foreground">Thirty minutes to decide whether a workflow should be automated, simplified or left alone.</p>
          </div>
          <div className="mt-10 grid gap-5 md:grid-cols-3">
            {STEPS.map((s) => (
              <article key={s.t} className="card-atk">
                <span className="icon-circle"><s.i className="h-5 w-5" /></span>
                <h3 className="mt-5 text-[18px] font-semibold text-navy">{s.t}</h3>
                <p className="mt-2 text-[15px] text-muted-foreground leading-relaxed">{s.b}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* APPS SHOWCASE */}
      <section id="apps" className="section-pad bg-white scroll-mt-24" data-section="apps">
        <div className="container-atk grid gap-10 lg:grid-cols-[1fr,1.1fr] items-center">
          <div className="max-w-xl">
            <span className="badge-pill">ATK AppsHub</span>
            <h2 className="mt-4 text-[30px] sm:text-[38px] lg:text-[44px] font-[750] text-navy">See what we've built.</h2>
            <p className="mt-4 text-[16px] text-muted-foreground leading-relaxed">
              Our own tools for scheduling, syncing sheets, planning and research. Built the same way we build for clients.
            </p>
            <div className="mt-7">
              <Button asChild className="rounded-full h-12 px-7 font-semibold" onClick={() => track("cta_home_apps_click")}>
                <a href="https://atkapps.com" target="_blank" rel="noopener">Explore our apps <ArrowRight className="ml-1 h-4 w-4" /></a>
              </Button>
            </div>
          </div>
          <AppsShowcase compact />
        </div>
      </section>

      {/* ALFRED CALLOUT */}
      <AlfredCallout source="home" />

      {/* FAQ */}
      <section id="faq" className="section-pad bg-white scroll-mt-24" data-section="faq">
        <div className="container-atk max-w-3xl">
          <h2 className="text-[30px] sm:text-[38px] lg:text-[44px] font-[750] text-navy">Frequently asked questions.</h2>
          <div className="mt-8"><FaqAccordion items={FAQ} /></div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="section-pad bg-soft" data-section="final-cta">
        <div className="container-atk max-w-3xl text-center">
          <h2 className="text-[30px] sm:text-[38px] lg:text-[44px] font-[750] text-navy">Pick the process that costs you the most time.</h2>
          <p className="mt-4 text-[16px] text-muted-foreground">We'll diagnose it and propose the smallest useful build. No retainer, no platform sell.</p>
          <Button asChild size="lg" className="mt-7 rounded-full h-12 px-7 font-semibold" data-cta="book-workflow-diagnosis" onClick={() => track("cta_final_book_click")}>
            <Link to="/book">Book a workflow diagnosis <ArrowRight className="ml-1 h-4 w-4" /></Link>
          </Button>
          <p className="mt-4 text-[13.5px] text-muted-foreground">30 minutes · No hard sell · Clear next step</p>
        </div>
      </section>
    </>
  );
}
